// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 28-it-dashboard-ui.md

/** The apps and users whose pending/granted grants block an integration or credential change. */
export interface BlockingGrants {
  apps: { id: string; name: string; slug: string }[];
  users: { id: string; email: string; display_name: string | null }[];
}

/**
 * Reads the in-use 409 an integration or credential guard throws. ts-rest
 * resolves a non-2xx response as a thrown `{ status, body }`. Returns null for
 * any other error. A matching code with malformed details still returns empty
 * lists, because the guard only fires while grants exist.
 */
export function blockingGrantsFromError(
  err: unknown,
  code: 'integration.in_use' | 'integration_credential.in_use',
): BlockingGrants | null {
  if (err === null || typeof err !== 'object' || !('body' in err)) return null;
  const body = (err as { body: unknown }).body;
  if (body === null || typeof body !== 'object' || (body as { code?: unknown }).code !== code) {
    return null;
  }
  const details = (body as { details?: unknown }).details;
  if (details === null || typeof details !== 'object') return { apps: [], users: [] };
  const { apps, users } = details as { apps?: unknown; users?: unknown };
  return {
    apps: Array.isArray(apps)
      ? apps.filter(
          (a): a is BlockingGrants['apps'][number] =>
            a != null &&
            typeof a === 'object' &&
            typeof (a as { name?: unknown }).name === 'string',
        )
      : [],
    users: Array.isArray(users)
      ? users.filter(
          (u): u is BlockingGrants['users'][number] =>
            u != null &&
            typeof u === 'object' &&
            typeof (u as { email?: unknown }).email === 'string',
        )
      : [],
  };
}

/** Display names for every blocking holder, apps first. */
export function blockingHolderNames(blocking: BlockingGrants): string[] {
  return [
    ...blocking.apps.map((a) => a.name),
    ...blocking.users.map((u) => u.display_name ?? u.email),
  ];
}

/** "2 apps and 1 person", "1 app", or null when the guard named no one. */
export function describeBlockingHolders(blocking: BlockingGrants): string | null {
  const parts: string[] = [];
  const { length: apps } = blocking.apps;
  const { length: users } = blocking.users;
  if (apps > 0) parts.push(`${apps} ${apps === 1 ? 'app' : 'apps'}`);
  if (users > 0) parts.push(`${users} ${users === 1 ? 'person' : 'people'}`);
  return parts.length > 0 ? parts.join(' and ') : null;
}
